import {
  Action,
  AdditionalProductActions,
  AddressAction,
  AddressActions,
  AuthAction,
  FetchPackageAction,
  PackagesActions,
  SalesAction,
  SalesActions,
  SalesApiAuthActions,
  SetAdditionalProduct,
  SetPackageAction,
  UpdateCustomerAction,
  UpdateCustomerActions
} from './actions';
import { RequestStatus } from '../util/RequestStatus';
import { ResetAction } from './resetActions';
import { AddressState, Product, SalesPackage, SalesPackageResponse, Selected } from '../types';
import { isUndefined } from '../util/utils';

export type AuthState = {
  token?: string;
};

export type State = {
  address: AddressState;
  auth: {
    token?: string;
  };
  packages: {
    requestStatus: RequestStatus;
    packages: {
      packages: Array<SalesPackage>;
      customerId?: number;
    };
    selectedPackage?: SalesPackage;
    selectedAdditionalProducts: Product[];
  };
  sale: {
    saleStatus: RequestStatus;
    zendeskTicketId?: string;
  };
  updateCustomer: {
    status: RequestStatus;
  };
};

export const initialState: State = {
  address: {
    addresses: {
      isFetching: false,
      items: []
    },
    selected: {
      house: undefined
    },
    requestNumber: 0,
    responseNumber: 0,
    selectedAddress: undefined
  },
  auth: {
    token: undefined
  },
  packages: {
    requestStatus: RequestStatus.INIT,
    packages: {
      packages: [],
      customerId: undefined
    },
    selectedPackage: undefined,
    selectedAdditionalProducts: []
  },
  sale: {
    saleStatus: RequestStatus.INIT,
    zendeskTicketId: undefined
  },
  updateCustomer: {
    status: RequestStatus.INIT
  }
};

const isAddressAction = (action: Action): action is AddressAction => {
  const { SELECT_VALID_ADDRESS } = AddressActions;

  return [SELECT_VALID_ADDRESS].includes(action.type);
};

const isAuthAction = (action: Action): action is AuthAction => {
  return action.type === SalesApiAuthActions.TOKEN_RESET || action.type === SalesApiAuthActions.TOKEN_SET;
};

const isFetchPackageAction = (action: Action): action is FetchPackageAction => {
  const { FETCH_PACKAGES_ERROR, FETCH_PACKAGES_SUCCESS, FETCH_PACKAGES_INIT } = PackagesActions;
  return [FETCH_PACKAGES_INIT, FETCH_PACKAGES_SUCCESS, FETCH_PACKAGES_ERROR].includes(action.type);
};
const isSetPackageAction = (action: Action): action is SetPackageAction => {
  const { RESET_PACKAGE, SELECT_PACKAGE, SET_5GANTENNA } = PackagesActions;

  return [RESET_PACKAGE, SELECT_PACKAGE, SET_5GANTENNA].includes(action.type);
};

const isSetAdditionalProductAction = (action: Action): action is SetAdditionalProduct => {
  const {
    RESET_SELECTED_ADDITIONAL_PRODUCTS,
    SELECT_ADDITIONAL_PRODUCT,
    DESELECT_ADDITIONAL_PRODUCT
  } = AdditionalProductActions;
  return [RESET_SELECTED_ADDITIONAL_PRODUCTS, SELECT_ADDITIONAL_PRODUCT, DESELECT_ADDITIONAL_PRODUCT].includes(
    action.type
  );
};

const isSalesAction = (action: Action): action is SalesAction => {
  const { COMMIT_SALE_ERROR, COMMIT_SALE_INIT, COMMIT_SALE_SUCCESS } = SalesActions;

  return [COMMIT_SALE_INIT, COMMIT_SALE_SUCCESS, COMMIT_SALE_ERROR].includes(action.type);
};

const isUpdateCustomerAction = (action: Action): action is UpdateCustomerAction => {
  const { UPDATE_CUSTOMER_ERROR, UPDATE_CUSTOMER_INIT, UPDATE_CUSTOMER_SUCCESS } = UpdateCustomerActions;

  return [UPDATE_CUSTOMER_INIT, UPDATE_CUSTOMER_SUCCESS, UPDATE_CUSTOMER_ERROR].includes(action.type);
};

export const reducer = (state: State, action: Action): State => {
  if (isAddressAction(action)) {
    const updatedAddressState = addressReducer(state, action);

    return {
      ...state,
      address: { ...updatedAddressState }
    };
  } else if (isSalesAction(action)) {
    const updatedSalesState = salesReducer(state, action);
    return {
      ...state,
      sale: { ...updatedSalesState }
    };
  } else if (isUpdateCustomerAction(action)) {
    const updatedCustomerState = updateCustomerReducer(state, action);
    return {
      ...state,
      updateCustomer: { ...updatedCustomerState }
    };
  } else if (isAuthAction(action)) {
    switch (action.type) {
      case SalesApiAuthActions.TOKEN_SET:
        return { ...state, auth: { ...state.auth, token: action.payload } };
      case SalesApiAuthActions.TOKEN_RESET:
        return { ...state, auth: { ...state.auth, token: undefined } };
      default:
        return state;
    }
  } else if (isFetchPackageAction(action)) {
    switch (action.type) {
      case PackagesActions.FETCH_PACKAGES_INIT:
        return { ...state, packages: { ...state.packages, requestStatus: RequestStatus.PENDING } };
      case PackagesActions.FETCH_PACKAGES_SUCCESS:
        const salesPackageResponse = action.payload as SalesPackageResponse;

        return {
          ...state,
          packages: {
            ...state.packages,
            requestStatus: RequestStatus.SUCCESS,
            packages: {
              ...state.packages.packages,
              customerId: salesPackageResponse.customerId,
              packages: salesPackageResponse.salesPackages
            }
          }
        };

      case PackagesActions.FETCH_PACKAGES_ERROR:
        return {
          ...state,
          packages: {
            ...state.packages,
            requestStatus: RequestStatus.ERROR,
            packages: {
              packages: [],
              customerId: undefined
            }
          }
        };
      default:
        return state;
    }
  } else if (isSetPackageAction(action)) {
    switch (action.type) {
      case PackagesActions.SELECT_PACKAGE:
        return { ...state, packages: { ...state.packages, selectedPackage: action.payload } };
      case PackagesActions.RESET_PACKAGE:
        return { ...state, packages: { ...state.packages, selectedPackage: undefined } };
      // case PackagesActions.SET_5GANTENNA:
      //   return { ...state, packages: { ...state.packages, fiveGAntenna: action.payload } };
      default:
        return state;
    }
  } else if (isSetAdditionalProductAction(action)) {
    switch (action.type) {
      case AdditionalProductActions.SELECT_ADDITIONAL_PRODUCT:
        const updatedAdditionalProductList = [...state.packages.selectedAdditionalProducts, action.payload];

        return { ...state, packages: { ...state.packages, selectedAdditionalProducts: updatedAdditionalProductList } };
      case AdditionalProductActions.RESET_SELECTED_ADDITIONAL_PRODUCTS:
        return { ...state, packages: { ...state.packages, selectedAdditionalProducts: [] } };
      case AdditionalProductActions.DESELECT_ADDITIONAL_PRODUCT:
        const selectedAdditionalProducts = state.packages.selectedAdditionalProducts;

        const updatedSelectedAdditionalProducts = selectedAdditionalProducts.filter(
          prod => prod.commercialProductId !== action.payload.commercialProductId
        );

        return {
          ...state,
          packages: {
            ...state.packages,
            selectedAdditionalProducts: updatedSelectedAdditionalProducts
          }
        };
    }
  } else {
    return state;
  }
  return state;
};

const addressReducer = (entireState: any, action: AddressAction | ResetAction) => {
  const state = entireState.address;
  switch (action.type) {
    case 'SELECT_VALID_ADDRESS':
      return {
        ...state,
        selectedAddress: action.address
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const salesReducer = (entireState: State, action: SalesAction) => {
  const state = entireState.sale;

  switch (action.type) {
    case SalesActions.COMMIT_SALE_INIT:
      return {
        ...state,
        saleStatus: RequestStatus.PENDING
      };
    case SalesActions.COMMIT_SALE_SUCCESS:
      return {
        ...state,
        saleStatus: RequestStatus.SUCCESS,
        zendeskTicketId: action.payload
      };
    case SalesActions.COMMIT_SALE_ERROR:
      return {
        ...state,
        saleStatus: RequestStatus.ERROR
      };
    default:
      return state;
  }
};

const updateCustomerReducer = (entireState: State, action: UpdateCustomerAction) => {
  const state = entireState.updateCustomer;

  switch (action.type) {
    case UpdateCustomerActions.UPDATE_CUSTOMER_INIT:
      return {
        ...state,
        status: RequestStatus.PENDING
      };
    case UpdateCustomerActions.UPDATE_CUSTOMER_SUCCESS:
      return {
        ...state,
        status: RequestStatus.SUCCESS
      };
    case UpdateCustomerActions.UPDATE_CUSTOMER_ERROR:
      return {
        ...state,
        status: RequestStatus.ERROR
      };
    default:
      return state;
  }
};
